import { auth } from "./api.ts";

/**
 * Offline mutation outbox (D-8): item edits made without a connection are
 * queued locally and replayed in order when the network returns. Conflict
 * policy per architecture §3.1: the server wins ties — any HTTP response
 * (including 409) retires the queued op; only a network failure keeps it.
 */
interface QueuedOp {
  path: string;
  method: string;
  body: unknown;
  queuedAt: number;
}

const KEY = "instead.outbox";

function load(): QueuedOp[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "[]") as QueuedOp[];
  } catch {
    return [];
  }
}

function save(ops: QueuedOp[]) {
  localStorage.setItem(KEY, JSON.stringify(ops));
}

export const outbox = {
  pending: (): number => load().length,

  enqueue(op: Omit<QueuedOp, "queuedAt">) {
    save([...load(), { ...op, queuedAt: Date.now() }]);
  },

  /** Replays in order; stops at the first network failure (still offline). */
  async flush(): Promise<number> {
    let ops = load();
    let replayed = 0;
    while (ops.length > 0) {
      const op = ops[0]!;
      try {
        await fetch(`/api${op.path}`, {
          method: op.method,
          headers: {
            ...(auth.token() ? { authorization: `Bearer ${auth.token()}` } : {}),
            "content-type": "application/json",
          },
          body: JSON.stringify(op.body),
        });
      } catch {
        break; // network still down — keep the rest queued
      }
      ops = ops.slice(1); // any HTTP response retires the op (server wins ties)
      save(ops);
      replayed += 1;
    }
    return replayed;
  },
};

// replay whenever connectivity returns, and once on boot in case ops are stale
if (typeof window !== "undefined") {
  window.addEventListener("online", () => void outbox.flush());
  if (navigator.onLine) void outbox.flush();
}
